import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SnowflakeAccountCard from 'features/snowflake-accounts/components/SnowflakeAccountCard';
import SnowflakeAccountsBar from 'features/snowflake-accounts/navigation/SnowflakeAccountsBar';
import SnowflakeCredentialsForm from 'features/snowflake-accounts/components/SnowflakeCredentialsForm';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { fetchAccounts, selectAccounts } from 'features/snowflake-accounts/reducers/snowflakeAccountsSlice';
import { resetOnboarding } from 'features/onboarding/reducers/onboardingSlice';
import SnowflakeIcon from 'components/SnowflakeIcon';

export interface ISnowflakeAccount {
  account: string;
  username: string;
  warehouse: string;
  role: string;
  id: number;
  is_editable: boolean;
  is_mfa: boolean;
  type: string;
  oauth_integration: number | undefined;
}

export default function SnowflakeAccountsView(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const accounts = useAppSelector(selectAccounts);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);

  const showError = false;
  const setShowError = (x: boolean): string => {
    return '';
  };
  const errorMessage = 'Update failed';
  const setErrorMessage = (x: string): string => {
    return '';
  };

  useEffect(() => {
    dispatch(fetchAccounts()).catch((err) => err);
  }, [accountDialogOpen]);

  const handleSetupClick = (): void => {
    dispatch(resetOnboarding());
    navigate('/onboarding', { state: { step: 'snowflake_setup' } });
  };

  return (
    <>
      <div className="h-full min-w-0 flex-1">
        <SnowflakeAccountsBar setAccountDialogOpen={setAccountDialogOpen} objectText="account" />
        {showError && (
          <>
            <div className="fixed top-[7.2rem] left-16 right-0 z-10 rounded-md bg-red-50 p-4">
              <div className="flex w-full">
                <div className="ml-3 w-full">
                  <div className="flex">
                    <h3 className="flex-grow text-sm font-medium text-red-800">Update error</h3>
                    <button
                      type="button"
                      onClick={() => setShowError(false)}
                      className="h-5 w-5 mr-4 inline-flex items-center rounded-full text-red-800 hover:bg-red-100"
                    >
                      <XMarkIcon className="w-full" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="w-full text-sm text-red-800">{errorMessage}</div>
                </div>
              </div>
            </div>
            <div className="mt-[7.5rem]"></div>
          </>
        )}
        <div className="w-full h-full min-w-0 mt-[3.2rem] sm:p-6 lg:p-8 bg-dashboard-background">
          {accounts.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md px-4">
                <div className="mx-auto flex items-center justify-center h-16 w-16 mb-6">
                  <SnowflakeIcon size="h-16" className="text-dataops-primary-blue" />
                </div>
                <h3 className="text-xl  text-dataops-dark mb-2">No Snowflake Accounts Connected</h3>
                <p className="text-secondary-text mb-6">Get started by connecting your Snowflake account.</p>
                <button
                  type="button"
                  onClick={handleSetupClick}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-dataops-primary-blue hover:bg-hover-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dataops-primary-blue transition-colors"
                  aria-label="Start onboarding to set up Snowflake account"
                >
                  Connect
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 wide:grid-cols-4 auto-rows-[26rem]">
              {accounts.map((account) => (
                <SnowflakeAccountCard
                  key={account.account}
                  data={account}
                  setShowError={setShowError}
                  setErrorMessage={setErrorMessage}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <SnowflakeCredentialsForm accountDialogOpen={accountDialogOpen} setAccountDialogOpen={setAccountDialogOpen} />
    </>
  );
}
